import { FormControl, MenuItem, Select, SelectChangeEvent, Typography, useTheme } from '@mui/material';
import React from 'react';

import { colors } from '../theme/theme';

interface SelectMenuProps {
    value: string;
    onChange: (value: string) => void;
    options: string[];
    placeholder?: string;
    /** Accessible name for the select (e.g. "Contract", "Status"). Used for aria-label and optionally for a visible label. */
    label?: string;
    selectMaxWidth?: string | number;
    selectMinWidth?: string | number;
    maxHeight?: string | number;
    iconColor?: string;
}

const SelectIcon = (props: React.HTMLAttributes<HTMLSpanElement>) => (
    <span
        {...props}
        className={['icon-arrow-drop-down', props.className].filter(Boolean).join(' ')}
        style={{ fontSize: 16, color: colors.fullBlack, cursor: 'pointer', ...props.style }}
    />
);

export const SelectMenu: React.FC<SelectMenuProps> = ({
    value,
    onChange,
    options,
    placeholder = 'Select',
    label,
    selectMaxWidth = 110,
    selectMinWidth = 110,
    maxHeight = 130,
    iconColor = colors.fullBlack,
}) => {
    const theme = useTheme();
    const selectId = React.useId();
    const labelId = `${selectId}-label`;

    const handleChange = (e: SelectChangeEvent) => {
        onChange(e.target.value as string);
    };

    return (
        <FormControl sx={{ maxWidth: selectMaxWidth, minWidth: selectMinWidth }}>
            {label && (
                <span
                    id={labelId}
                    style={{
                        position: 'absolute',
                        width: 1,
                        height: 1,
                        padding: 0,
                        margin: -1,
                        overflow: 'hidden',
                        clip: 'rect(0,0,0,0)',
                        whiteSpace: 'nowrap',
                        border: 0,
                    }}
                >
                    {label}
                </span>
            )}
            <Select
                id={selectId}
                value={value}
                onChange={handleChange}
                displayEmpty
                IconComponent={SelectIcon}
                inputProps={{
                    ...(label ? { 'aria-labelledby': labelId } : { 'aria-label': placeholder }),
                }}
                renderValue={selected =>
                    selected ? (
                        <Typography
                            variant="body2"
                            color={theme.palette.text.primary}
                            noWrap
                            sx={{ lineHeight: '21px' }}
                        >
                            {selected}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="textSecondary" noWrap sx={{ lineHeight: '21px' }}>
                            {placeholder}
                        </Typography>
                    )
                }
                sx={{
                    borderRadius: theme.spacing(1),
                    p: 1,
                    color: theme.palette.text.primary,
                    backgroundColor: colors.fullWhite,
                    '& .MuiOutlinedInput-notchedOutline': {
                        // border: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        // border: 'none',
                    },
                    '& .MuiOutlinedInput-input': {
                        p: 0,
                        pr: '8px !important',
                        display: 'flex',
                        alignItems: 'center',
                    },
                    '&.MuiInputBase-root': {
                        '& .MuiSelect-icon': {
                            color: iconColor,
                        },
                        '& .MuiSelect-select': {
                            minHeight: 1,
                        },
                    },
                }}
                MenuProps={{
                    PaperProps: {
                        role: 'region',
                        ...(label ? { 'aria-labelledby': labelId } : { 'aria-label': placeholder }),
                        sx: {
                            backgroundColor: colors.fullWhite,
                            mt: 1.25,
                            borderRadius: theme.spacing(1),
                            boxShadow: theme.shadows[2],
                            border: `1px solid ${colors.greyPreset[30]}`,
                            overflow: 'hidden',
                            '& .MuiList-root': {
                                gap: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                maxHeight: maxHeight,
                                overflow: 'auto',
                            },
                        },
                    },
                }}
            >
                {options.map(item => (
                    <MenuItem
                        key={item}
                        value={item}
                        sx={{
                            ...theme.typography.body2,
                            color: theme.palette.text.primary,
                            py: 1,
                            px: 2.5,

                            '&:hover': {
                                backgroundColor: colors.primaryLight[5],
                            },

                            '&.Mui-selected': {
                                backgroundColor: `${colors.primaryLight[5]} !important`,
                                color: theme.palette.text.primary,
                            },
                        }}
                    >
                        {item}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};
